import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import {
  Download, ArrowRight, ShieldCheck, AlertTriangle, Info, Copy,
  Calendar, Mail, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { TopBar, Footer } from "@/components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { fr } from "@/lib/format";
import { downloadPdf, fetchConfig, fetchReferenceData } from "@/lib/api";

const CHART_COLORS = ["#DE0F72", "#FF9900", "#8A0E9E", "#9E0E40", "#94A3B8"];

function KPI({ label, value, sub, testId, tone = "default" }) {
  const tint = tone === "primary" ? "border-brand-fuchsia" : "border-slate-200";
  return (
    <div className={`kpi ${tint}`} data-testid={testId}>
      <div className="label-caps">{label}</div>
      <div className="mt-1 text-2xl md:text-3xl font-bold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500 leading-snug">{sub}</div>}
    </div>
  );
}

function SectionTitle({ eyebrow, title, children }) {
  return (
    <div className="mb-6">
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h2 className="section-h mt-2">{title}</h2>
      {children && <p className="mt-2 text-slate-600 leading-relaxed max-w-3xl">{children}</p>}
    </div>
  );
}

function RiskCard({ r, i }) {
  const { t } = useTranslation();
  const critColor = {
    critique: "text-red-600 bg-red-50 border-red-200",
    élevée: "text-orange-600 bg-orange-50 border-orange-200",
    moyenne: "text-amber-600 bg-amber-50 border-amber-200",
    modérée: "text-slate-600 bg-slate-50 border-slate-200",
    faible: "text-slate-500 bg-slate-50 border-slate-200",
  }[r.criticality] || "text-slate-600 bg-slate-50 border-slate-200";
  return (
    <div className="card-flat p-6" data-testid={`risk-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-slate-400 text-xs font-semibold">{t("results.risk_priority", { n: i + 1 })}</div>
          <h3 className="mt-1 text-lg font-bold text-slate-900">{r.title}</h3>
        </div>
        <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-1 rounded-sm border ${critColor}`}>
          {r.criticality}
        </span>
      </div>
      <p className="mt-3 text-slate-600 leading-relaxed">{r.description}</p>
      <p className="mt-2 text-sm text-slate-500 italic">{t("results.risk_scenario")} : {r.scenario}</p>
      <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
        <div className="rounded-sm border border-slate-200 p-3">
          <div className="label-caps">{t("results.risk_imm")}</div>
          <div className="mt-1 text-slate-700">{r.immediate}</div>
        </div>
        <div className="rounded-sm border border-slate-200 p-3">
          <div className="label-caps">{t("results.risk_d90")}</div>
          <div className="mt-1 text-slate-700">{r.d90}</div>
        </div>
      </div>
      <div className="mt-4 rounded-sm border border-fuchsia-200 bg-fuchsia-50/40 p-3">
        <div className="label-caps text-brand-bordeaux">{t("results.risk_nx")}</div>
        <div className="mt-1 text-slate-700 text-sm">{r.netwrix_value}</div>
      </div>
      {r.complement && (
        <div className="mt-3 text-xs text-slate-500">{t("results.risk_complement")} <b>{r.complement}</b></div>
      )}
    </div>
  );
}

export default function Results({ dataOverride, isExample = false }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").split("-")[0];
  const navigate = useNavigate();
  const [study, setStudy] = useState(dataOverride || null);
  const [config, setConfig] = useState(null);
  const [refData, setRefData] = useState(null);
  const [scenario, setScenario] = useState("central");

  useEffect(() => {
    if (!study) {
      try {
        const raw = sessionStorage.getItem("ariovis_ad_result");
        if (raw) setStudy(JSON.parse(raw));
      } catch (e) { /* ignore */ }
    }
    fetchConfig().then(setConfig).catch(() => {});
    fetchReferenceData().then(setRefData).catch(() => {});
  }, [study, lang]);

  const roi = study?.roi?.scenarios?.[scenario];

  const dimensionsData = useMemo(() => {
    if (!study) return [];
    const labelsFr = {
      governance: "Gouvernance", hygiene: "Hygiène", privileges: "Privilèges",
      monitoring: "Surveillance", investigation: "Investigation",
      compliance: "Conformité", resilience: "Résilience",
    };
    const labelsEn = {
      governance: "Governance", hygiene: "Hygiene", privileges: "Privileges",
      monitoring: "Monitoring", investigation: "Investigation",
      compliance: "Compliance", resilience: "Resilience",
    };
    const labels = lang === "en" ? labelsEn : labelsFr;
    return Object.entries(study.scores.dimensions).map(([k, v]) => ({ dim: labels[k], score: v }));
  }, [study, lang]);

  const cashflowData = useMemo(() => {
    if (!roi) return [];
    let cumBenefits = 0, cumCosts = 0;
    const yr = lang === "en" ? "Year" : "Année";
    const benL = lang === "en" ? "Benefits" : "Bénéfices";
    const costL = lang === "en" ? "Costs" : "Coûts";
    const benCum = lang === "en" ? "Cumulative benefits" : "Bénéfices cumulés";
    const costCum = lang === "en" ? "Cumulative costs" : "Coûts cumulés";
    return roi.yearly.map((y) => {
      cumBenefits += y.total_benefit;
      cumCosts += y.cost;
      return {
        year: `${yr} ${y.year}`,
        [benL]: Math.round(y.total_benefit),
        [costL]: Math.round(y.cost),
        [benCum]: Math.round(cumBenefits),
        [costCum]: Math.round(cumCosts),
      };
    });
  }, [roi, lang]);

  const projectPie = useMemo(() => {
    if (!study) return [];
    const p = study.project;
    const names = lang === "en"
      ? ["Scoping & governance", "Install & configuration", "Scenarios & alerts", "UAT & go-live", "Training & documentation"]
      : ["Cadrage & pilotage", "Installation & config", "Scénarios & alertes", "Recette & MEP", "Formation & documentation"];
    return [
      { name: names[0], value: Math.max(1, Math.round(p.days_central * 0.15)) },
      { name: names[1], value: Math.round(p.days_central * 0.35) },
      { name: names[2], value: Math.round(p.days_central * 0.20) },
      { name: names[3], value: Math.round(p.days_central * 0.15) },
      { name: names[4], value: Math.round(p.days_central * 0.15) },
    ];
  }, [study, lang]);

  const benefitsPie = useMemo(() => {
    if (!roi) return [];
    const b = roi.bucket_a;
    const names = lang === "en"
      ? ["Recurring monitoring", "Investigations", "Reports & audits", "Direct costs avoided", "Risk avoided (annual)"]
      : ["Surveillance récurrente", "Investigations", "Rapports & audits", "Coûts directs évités", "Risque évité (annuel)"];
    return [
      { name: names[0], value: b.monitoring },
      { name: names[1], value: b.investigations },
      { name: names[2], value: b.audit_recurring + b.audit_adhoc },
      { name: names[3], value: roi.bucket_b_annual },
      { name: names[4], value: roi.bucket_c.risk_avoided_annual },
    ].filter(d => d.value > 0);
  }, [roi, lang]);

  if (!study) {
    return (
      <div className="min-h-screen bg-slate-50">
        <TopBar />
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h2 className="section-h">{t("results.empty_title")}</h2>
          <p className="mt-3 text-slate-600">{t("results.empty_sub")}</p>
          <button onClick={() => navigate("/simuler")} className="btn-primary mt-8" data-testid="empty-cta">
            {t("results.empty_cta")} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const scores = study.scores;
  const project = study.project;
  const netwrix = study.netwrix;
  const calendarUrl = config?.calendar_url;

  const roiIsNegative = (roi?.roi_pct ?? 0) < 0;
  const roiKpi = roiIsNegative
    ? { label: t("results.kpi_roi_neu"), value: t("results.kpi_roi_neu_val"), sub: t("results.kpi_roi_neu_sub") }
    : { label: t("results.kpi_roi_pos"), value: fr.percent(roi.roi_pct, 1), sub: `${t("results.roi_van")} : ${fr.currency(roi.npv)}` };

  const positiveOrNeutral = (v, formatter = fr.currency) => (v < 0 ? t("results.roi_van_neutre") : formatter(v));
  const positiveRoi = (v) => (v < 0 ? t("results.roi_van_neutre") : fr.percent(v, 1));

  const doDownload = async () => {
    try {
      const blob = await downloadPdf({ inputs: study.inputs, lead: study.lead });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "diagnostic-ad-ariovis.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e) {
      toast.error(t("results.download_fail"));
    }
  };

  const copyResumeLink = async () => {
    const url = `${window.location.origin}${process.env.PUBLIC_URL || ""}/simuler?reprise=${study.resume_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("results.resume_copied"));
    } catch { toast.error(t("results.copy_fail")); }
  };

  const risksSubLabel = lang === "en"
    ? `${study.risks.length} risks in total`
    : `${study.risks.length} risques identifiés au total`;
  const daysSubLabel = lang === "en"
    ? `${project.days_low} – ${project.days_high} d (range)`
    : `${project.days_low} – ${project.days_high} j (intervalle)`;
  const servicesSub = lang === "en" ? "ex-tax · indicative" : "HT · indicatif";
  const budgetTotalSubDefault = lang === "en" ? "services + licences" : "services + licences";

  const roadmapBlocks = [
    { title: t("results.roadmap_imm"), subtitle: t("results.roadmap_imm_sub"), key: "immediate", tone: "border-red-200 bg-red-50/40" },
    { title: t("results.roadmap_d30"), subtitle: t("results.roadmap_d30_sub"), key: "d30", tone: "border-orange-200 bg-orange-50/40" },
    { title: t("results.roadmap_d90"), subtitle: t("results.roadmap_d90_sub"), key: "d90", tone: "border-amber-200 bg-amber-50/40" },
    { title: t("results.roadmap_d180"), subtitle: t("results.roadmap_d180_sub"), key: "d180", tone: "border-slate-200 bg-slate-50" },
  ];

  const sec5Features = t("results.sec5_features", { returnObjects: true }) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar />

      <section className="bg-slate-950 text-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-14">
          <div className="eyebrow text-brand-fuchsia">{t("results.hero_eyebrow")}</div>
          <h1 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight" data-testid="results-title">
            {study.lead?.company || t("results.hero_org_default")} {t("results.hero_title_suffix")}
          </h1>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <KPI label={t("results.kpi_score")} value={`${scores.global}/100`} sub={`${t(`results.levels.${scores.level}`, { defaultValue: scores.level })} · ${t(`results.confidence.${scores.confidence}`, { defaultValue: scores.confidence })}`} testId="kpi-score" tone="primary" />
            <KPI label={t("results.kpi_risks")} value={study.top_risks.length} sub={risksSubLabel} testId="kpi-risks" />
            <KPI label={t("results.kpi_days")} value={`${project.days_central} ${lang === "en" ? "d" : "j"}`} sub={daysSubLabel} testId="kpi-days" />
            <KPI label={t("results.kpi_services")} value={fr.currency(project.services_central)} sub={servicesSub} testId="kpi-services" />
            <KPI label={t("results.kpi_budget_total")} value={study.budget?.total_y1_central != null ? fr.currency(study.budget.total_y1_central) : "—"} sub={study.budget?.total_y1_low != null ? `${fr.currency(study.budget.total_y1_low)} – ${fr.currency(study.budget.total_y1_high)}` : budgetTotalSubDefault} testId="kpi-budget-total" tone="primary" />
            <KPI label={roiKpi.label} value={roiKpi.value} sub={roiKpi.sub} testId="kpi-roi" tone={roiIsNegative ? "primary" : "default"} />
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <button onClick={doDownload} className="btn-primary" data-testid="download-pdf">
              <Download className="h-4 w-4" /> {t("results.download")}
            </button>
            {calendarUrl && (
              <a href={calendarUrl} target="_blank" rel="noreferrer" className="btn-ghost border-slate-700 text-slate-100 hover:text-brand-fuchsia" data-testid="cta-calendar">
                <Calendar className="h-4 w-4" /> {t("results.calendar")}
              </a>
            )}
            {!isExample && study.resume_token && (
              <button onClick={copyResumeLink} className="btn-ghost border-slate-700 text-slate-100 hover:text-brand-fuchsia" data-testid="copy-resume">
                <Copy className="h-4 w-4" /> {t("results.copy_resume")}
              </button>
            )}
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-6 py-16 space-y-20">

        {/* Synthesis + Environment */}
        <section>
          <SectionTitle eyebrow={t("results.sec1_eyebrow")} title={t("results.sec1_title")} />
          {roiIsNegative && roi.bucket_c.risk_avoided_annual > 0 && (
            <div className="mb-6 rounded-xl border border-brand-fuchsia bg-fuchsia-50/50 p-6" data-testid="risk-focus-banner">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-brand-fuchsia flex items-center justify-center shrink-0">
                  <ShieldCheck className="h-5 w-5 text-white" />
                </div>
                <div>
                  <div className="text-brand-bordeaux text-[11px] font-semibold uppercase tracking-widest">{t("results.risk_focus_eyebrow")}</div>
                  <h3 className="mt-1 text-lg font-bold text-slate-900">
                    {t("results.risk_focus_title")}
                  </h3>
                  <p className="mt-2 text-slate-700 leading-relaxed text-sm">
                    {t("results.risk_focus_body", {
                      risk: fr.currency(roi.bucket_c.risk_avoided_annual),
                      ale: fr.currency(roi.bucket_c.ale),
                      disc: fr.currency(roi.totals.discounted_benefits),
                    })}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    {t("results.risk_focus_note")}
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 card-flat p-6">
              <p className="text-slate-700 leading-relaxed">
                {lang === "en" ? (
                  <>Based on your inputs, your Active Directory scores an overall maturity of <b>{scores.global}/100</b> ({t(`results.levels.${scores.level}`, { defaultValue: scores.level })}). {study.top_risks.length} priority workstreams have been identified. The recommended deployment is <b>{netwrix.package}</b>.</>
                ) : (
                  <>Sur la base de vos déclarations, votre Active Directory présente une maturité globale de <b>{scores.global}/100</b> ({scores.level}). {study.top_risks.length} chantiers prioritaires ont été identifiés. Le déploiement recommandé est <b>{netwrix.package}</b>.</>
                )}
              </p>
              <p className="text-sm text-slate-500 mt-3 leading-relaxed">{netwrix.reason}</p>
              {netwrix.complementary_package && (
                <p className="text-sm text-brand-bordeaux mt-2">{netwrix.complementary_package}</p>
              )}
              <p className="text-xs text-slate-500 mt-4">{netwrix.price_disclaimer}</p>
            </div>
            <div className="card-flat p-6">
              <div className="label-caps">{t("results.env_label")}</div>
              <dl className="mt-3 text-sm text-slate-700 grid grid-cols-2 gap-2">
                <dt className="text-slate-500">{t("results.env_size")}</dt><dd>{refData?.sizes?.find(s => s.id === study.inputs?.company?.size)?.label || study.inputs?.company?.size}</dd>
                <dt className="text-slate-500">{t("results.env_id")}</dt><dd>{fr.number(study.inputs?.company?.identities)}</dd>
                <dt className="text-slate-500">{t("results.env_fd")}</dt><dd>{study.inputs?.architecture?.forests} / {study.inputs?.architecture?.domains}</dd>
                <dt className="text-slate-500">{t("results.env_dep")}</dt><dd>{refData?.deployment?.find(d => d.id === study.inputs?.architecture?.deployment)?.label || study.inputs?.architecture?.deployment}</dd>
                <dt className="text-slate-500">{t("results.env_priv")}</dt><dd>{study.inputs?.architecture?.privileged_accounts}</dd>
                <dt className="text-slate-500">{t("results.env_svc")}</dt><dd>{study.inputs?.architecture?.service_accounts}</dd>
              </dl>
            </div>
          </div>
        </section>

        {/* Radar / dimensions */}
        <section>
          <SectionTitle eyebrow={t("results.sec2_eyebrow")} title={t("results.sec2_title")}>
            {t("results.sec2_sub", { fn: study.inputs?.profile?.function || "—" })}
          </SectionTitle>
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="card-flat p-6" style={{ height: 380 }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={dimensionsData}>
                  <PolarGrid stroke="#e2e8f0" />
                  <PolarAngleAxis dataKey="dim" tick={{ fill: "#334155", fontSize: 12 }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <Radar name="Score" dataKey="score" stroke="#DE0F72" fill="#DE0F72" fillOpacity={0.35} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="card-flat p-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-500 uppercase text-[11px] tracking-wider">
                    <th className="text-left pb-3">{t("results.tbl_dim")}</th>
                    <th className="text-right pb-3">{t("results.tbl_score")}</th>
                    <th className="text-right pb-3">{t("results.tbl_level")}</th>
                  </tr>
                </thead>
                <tbody>
                  {dimensionsData.map((d) => (
                    <tr key={d.dim} className="border-t border-slate-100">
                      <td className="py-3 text-slate-800">{d.dim}</td>
                      <td className="py-3 text-right font-semibold text-slate-900">{d.score}/100</td>
                      <td className="py-3 text-right text-brand-bordeaux font-semibold">
                        {t(`results.levels.${study.scores.dimension_levels[Object.keys(study.scores.dimensions).find(k => study.scores.dimensions[k] === d.score)]}`, { defaultValue: study.scores.dimension_levels[Object.keys(study.scores.dimensions).find(k => study.scores.dimensions[k] === d.score)] })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Top risks */}
        <section>
          <SectionTitle eyebrow={t("results.sec3_eyebrow")} title={t("results.sec3_title")} />
          <div className="grid lg:grid-cols-2 gap-6">
            {study.top_risks.map((r, i) => <RiskCard key={r.id} r={r} i={i} />)}
          </div>
        </section>

        {/* Roadmap */}
        <section>
          <SectionTitle eyebrow={t("results.sec4_eyebrow")} title={t("results.sec4_title")} />
          <div className="grid lg:grid-cols-4 gap-6">
            {roadmapBlocks.map((h) => (
              <div key={h.key} className={`rounded-sm border p-5 ${h.tone}`} data-testid={`roadmap-${h.key}`}>
                <div className="label-caps">{h.title}</div>
                <div className="text-slate-500 text-xs">{h.subtitle}</div>
                <ul className="mt-4 space-y-2 text-sm text-slate-700">
                  {study.roadmap[h.key].map((item, i) => (
                    <li key={i} className="flex gap-2"><span className="text-brand-fuchsia">›</span> {item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Netwrix + complémentaires */}
        <section>
          <SectionTitle eyebrow={t("results.sec5_eyebrow")} title={t("results.sec5_title")} />
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 card-flat p-6">
              <h3 className="text-lg font-bold text-slate-900">{netwrix.package}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{netwrix.reason}</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-slate-700">
                {sec5Features.map((f) => <li key={f} className="flex gap-2 items-start"><ShieldCheck className="h-3.5 w-3.5 text-brand-fuchsia mt-1" /> {f}</li>)}
              </ul>
              <p className="mt-4 text-xs text-slate-500">{netwrix.price_disclaimer}</p>
            </div>
            <div className="card-flat p-6">
              <div className="label-caps">{t("results.sec5_comp")}</div>
              {study.complementary_products?.length ? (
                <ul className="mt-3 space-y-3 text-sm">
                  {study.complementary_products.map((p) => (
                    <li key={p.product}>
                      <div className="font-semibold text-slate-900">{p.product}</div>
                      <div className="text-slate-600 text-xs">{p.reason}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-slate-500">{t("results.sec5_none")}</p>
              )}
            </div>
          </div>
        </section>

        {/* Project plan */}
        <section>
          <SectionTitle eyebrow={t("results.sec6_eyebrow")} title={`${project.tier.name} · ${project.days_central} ${lang === "en" ? "days" : "jours"} (${lang === "en" ? "range" : "intervalle"} ${project.days_low}–${project.days_high})`}>
            {t("results.sec6_intro")}
          </SectionTitle>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 card-flat p-6">
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="label-caps">{t("results.sec6_tier")}</div>
                  <div className="mt-1 font-semibold text-slate-900">{project.tier.name}</div>
                </div>
                <div>
                  <div className="label-caps">{t("results.sec6_budget")}</div>
                  <div className="mt-1 font-semibold text-slate-900">{fr.currency(project.services_central)} {lang === "en" ? "ex-tax" : "HT"}</div>
                  <div className="text-xs text-slate-500">{t("results.sec6_rate")} {fr.currency(project.daily_rate)}</div>
                </div>
                <div>
                  <div className="label-caps">{t("results.sec6_range")}</div>
                  <div className="mt-1 font-semibold text-slate-900">{fr.currency(project.services_low)} – {fr.currency(project.services_high)}</div>
                </div>
              </div>
              <div className="mt-6 grid md:grid-cols-2 gap-4 text-sm">
                <div className="rounded-sm border border-slate-200 p-4">
                  <div className="label-caps">{t("results.sec6_up")}</div>
                  <ul className="mt-2 space-y-1 text-slate-700">
                    {(project.adjustments_up.length ? project.adjustments_up : [t("results.sec6_up_none")]).map((a) => <li key={a}>+ {a}</li>)}
                  </ul>
                </div>
                <div className="rounded-sm border border-slate-200 p-4">
                  <div className="label-caps">{t("results.sec6_down")}</div>
                  <ul className="mt-2 space-y-1 text-slate-700">
                    {(project.adjustments_down.length ? project.adjustments_down : [t("results.sec6_down_none")]).map((a) => <li key={a}>− {a}</li>)}
                  </ul>
                </div>
              </div>
              <Accordion type="single" collapsible className="mt-6">
                <AccordionItem value="deliverables">
                  <AccordionTrigger className="hover:no-underline">{t("results.sec6_deliv")}</AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-1 text-sm text-slate-700">{project.tier.deliverables.map((d) => <li key={d}>› {d}</li>)}</ul>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="assumptions">
                  <AccordionTrigger className="hover:no-underline">{t("results.sec6_assump")}</AccordionTrigger>
                  <AccordionContent>
                    <div className="text-sm text-slate-700">
                      <div className="label-caps mb-2">{t("results.sec6_hyp")}</div>
                      <ul className="space-y-1">{project.assumptions.map((a) => <li key={a}>› {a}</li>)}</ul>
                      <div className="label-caps mt-4 mb-2">{t("results.sec6_excl")}</div>
                      <ul className="space-y-1">{project.exclusions.map((e) => <li key={e}>› {e}</li>)}</ul>
                    </div>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="respo">
                  <AccordionTrigger className="hover:no-underline">{t("results.sec6_respo")}</AccordionTrigger>
                  <AccordionContent>
                    <div className="grid sm:grid-cols-3 gap-4 text-sm">
                      {Object.entries(project.responsibilities).map(([k, arr]) => (
                        <div key={k}>
                          <div className="label-caps">{k}</div>
                          <ul className="mt-2 space-y-1 text-slate-700">{arr.map((x) => <li key={x}>› {x}</li>)}</ul>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
            <div className="card-flat p-6" style={{ minHeight: 320 }}>
              <div className="label-caps">{t("results.sec6_pie")}</div>
              <div className="mt-2 h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={projectPie} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                      {projectPie.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${v} ${lang === "en" ? "d" : "j"}`} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="text-xs text-slate-600 space-y-1 mt-2">
                {projectPie.map((s, i) => (
                  <li key={s.name} className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    {s.name} — {s.value} {lang === "en" ? "d" : "j"}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Budget détail */}
        <section>
          <SectionTitle eyebrow={t("results.sec6b_eyebrow")} title={t("results.sec6b_title")}>
            {t("results.sec6b_intro")}
          </SectionTitle>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="kpi" data-testid="budget-services-block">
              <div className="label-caps">{t("results.sec6b_services")}</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">{fr.currency(study.budget?.services_central)}</div>
              <div className="mt-1 text-xs text-slate-500">{t("results.sec6b_days_at", { n: study.project.days_central, r: fr.currency(study.project.daily_rate) })}</div>
              <div className="mt-2 text-xs text-slate-500">{t("results.sec6_range")} : {fr.currency(study.budget?.services_low)} – {fr.currency(study.budget?.services_high)}</div>
            </div>
            <div className="kpi" data-testid="budget-licences-block">
              <div className="label-caps">{t("results.sec6b_licenses")}</div>
              <div className="mt-1 text-2xl font-bold text-slate-900">
                {study.budget?.licences_year1_eur != null ? fr.currency(study.budget.licences_year1_eur) : `${fr.number(study.budget?.licences_year1_usd)} USD`}
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {study.roi.scenarios.central.licenses_estimate.list_eur_per_identity != null
                  ? t("results.sec6b_lic_id", { eur: fr.number(study.roi.scenarios.central.licenses_estimate.effective_eur_per_identity, 2), n: fr.number(study.inputs?.company?.identities) })
                  : t("results.sec6b_lic_pub")}
              </div>
              {study.budget?.licences_discount_pct > 0 && (
                <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-fuchsia-50 border border-fuchsia-200 text-brand-bordeaux text-[11px] font-semibold px-2 py-0.5">
                  {t("results.sec6b_disc")} −{Math.round(study.budget.licences_discount_pct * 100)} %
                </div>
              )}
            </div>
            <div className="kpi border-brand-fuchsia" data-testid="budget-total-block">
              <div className="label-caps">{t("results.sec6b_total")}</div>
              <div className="mt-1 text-3xl font-bold text-slate-900">
                {study.budget?.total_y1_central != null ? fr.currency(study.budget.total_y1_central) : t("results.sec6b_total_devis")}
              </div>
              {study.budget?.total_y1_low != null && (
                <div className="mt-1 text-xs text-slate-500">{t("results.sec6_range")} : {fr.currency(study.budget.total_y1_low)} – {fr.currency(study.budget.total_y1_high)}</div>
              )}
              <div className="mt-2 text-xs text-slate-500 leading-relaxed">{t("results.sec6b_total_sub")}</div>
            </div>
          </div>
          {study.budget?.licences_discount_note && (
            <p className="mt-4 text-xs text-slate-500 max-w-3xl leading-relaxed">{study.budget.licences_discount_note}</p>
          )}
        </section>

        {/* ROI */}
        <section>
          <SectionTitle eyebrow={t("results.sec7_eyebrow")} title={t("results.sec7_title")}>
            {t("results.sec7_intro")}
          </SectionTitle>
          <Tabs value={scenario} onValueChange={setScenario} className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="prudent" data-testid="tab-prudent">{t("results.tab_prudent")}</TabsTrigger>
              <TabsTrigger value="central" data-testid="tab-central">{t("results.tab_central")}</TabsTrigger>
              <TabsTrigger value="ambitieux" data-testid="tab-ambitieux">{t("results.tab_ambitieux")}</TabsTrigger>
            </TabsList>
            {["prudent", "central", "ambitieux"].map((s) => {
              const sc = study.roi.scenarios[s];
              const monthsWord = lang === "en" ? "months" : "mois";
              const paybackTxt = sc.roi_pct < 0
                ? t("results.roi_payback_neu")
                : t("results.roi_payback", { v: sc.payback_month_cash ? `${sc.payback_month_cash} ${monthsWord}` : t("results.roi_payback_out") });
              const benL = lang === "en" ? "Cumulative benefits" : "Bénéfices cumulés";
              const costL = lang === "en" ? "Cumulative costs" : "Coûts cumulés";
              return (
                <TabsContent key={s} value={s}>
                  <div className="grid md:grid-cols-4 gap-4">
                    <KPI label={t("results.roi_disc_ben")} value={fr.currency(sc.totals.discounted_benefits)} />
                    <KPI label={t("results.roi_disc_cost")} value={fr.currency(sc.totals.discounted_costs)} />
                    <KPI label={t("results.roi_van")} value={positiveOrNeutral(sc.npv)} tone={sc.npv >= 0 ? "primary" : "default"} />
                    <KPI label={t("results.roi_roi")} value={positiveRoi(sc.roi_pct)} sub={paybackTxt} />
                  </div>

                  <div className="mt-6 grid lg:grid-cols-2 gap-6">
                    <div className="card-flat p-6" style={{ height: 340 }}>
                      <div className="label-caps mb-2">{t("results.roi_cumul")}</div>
                      <ResponsiveContainer width="100%" height="90%">
                        <LineChart data={cashflowData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="year" tick={{ fill: "#334155", fontSize: 12 }} />
                          <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                          <Tooltip formatter={(v) => fr.currency(v)} />
                          <Legend />
                          <Line type="monotone" dataKey={benL} stroke="#DE0F72" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey={costL} stroke="#64748b" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="card-flat p-6" style={{ height: 340 }}>
                      <div className="label-caps mb-2">{t("results.roi_compo")}</div>
                      <ResponsiveContainer width="100%" height="90%">
                        <BarChart data={benefitsPie} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                          <YAxis type="category" dataKey="name" tick={{ fill: "#334155", fontSize: 11 }} width={130} />
                          <Tooltip formatter={(v) => fr.currency(v)} />
                          <Bar dataKey="value" fill="#DE0F72" radius={[0, 3, 3, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="mt-6 card-flat p-6">
                    <div className="label-caps">{t("results.roi_formulas", { s: t(`results.tab_${s}`) })}</div>
                    <ul className="mt-3 space-y-1 text-sm text-slate-700 font-mono text-[13px]">
                      <li>• {lang === "en" ? "Monitoring" : "Surveillance"} : {sc.bucket_a.formulas.monitoring}</li>
                      <li>• {lang === "en" ? "Investigations" : "Investigations"} : {sc.bucket_a.formulas.investigations}</li>
                      <li>• {lang === "en" ? "Reports/audits" : "Rapports/audits"} : {sc.bucket_a.formulas.audit_recurring}</li>
                      <li>• {lang === "en" ? "Ad-hoc requests" : "Demandes ad hoc"} : {sc.bucket_a.formulas.audit_adhoc}</li>
                      <li>• {lang === "en" ? "Risk (ALE)" : "Risque (ALE)"} : {sc.bucket_c.formula}</li>
                    </ul>
                    <div className="mt-4 text-sm text-slate-700">
                      {t("results.roi_formula_final")}
                    </div>
                    {sc.warnings?.length > 0 && (
                      <div className="mt-4 rounded-sm border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-800 flex gap-2">
                        <AlertTriangle className="h-4 w-4 mt-0.5" />
                        <div>
                          <div className="font-semibold">{t("results.roi_warnings")}</div>
                          <ul className="mt-1 list-disc list-inside">
                            {sc.warnings.map((w, i) => <li key={i}>{w}</li>)}
                          </ul>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </section>

        {/* Retours d'expérience */}
        <section>
          <SectionTitle eyebrow={t("results.sec8_eyebrow")} title={t("results.sec8_title")}>
            {t("results.sec8_intro")}
          </SectionTitle>
          <div className="grid lg:grid-cols-2 gap-6">
            {study.anonymized_cases.slice(0, 6).map((c, i) => (
              <div key={i} className="card-flat p-5">
                <div className="text-sm font-bold text-slate-900">{c.context}</div>
                <p className="mt-2 text-slate-600 text-sm">{c.details}</p>
                <p className="mt-2 text-sm text-brand-bordeaux">{t("results.sec8_result")} {c.result}</p>
                <p className="mt-2 text-xs text-slate-400">{c.disclaimer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Sources & disclaimers */}
        <section>
          <SectionTitle eyebrow={t("results.sec9_eyebrow")} title={t("results.sec9_title")} />
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="card-flat p-6 text-sm text-slate-700">
              <div className="label-caps">{t("results.sec9_stats")}</div>
              <ul className="mt-3 space-y-2">
                {study.sources.map((s, i) => (
                  <li key={i}><b>{s.stat}</b> <span className="text-slate-400">({s.provenance}, {s.year})</span></li>
                ))}
              </ul>
            </div>
            <div className="card-flat p-6 text-sm text-slate-700">
              <div className="label-caps">{t("results.sec9_limits")}</div>
              <ul className="mt-3 space-y-3">
                {study.disclaimers.map((d, i) => (
                  <li key={i} className="flex gap-2"><Info className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" /> {d}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="rounded-sm border border-slate-200 bg-slate-950 text-white p-8 md:p-12">
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2">
              <div className="eyebrow text-brand-fuchsia">{t("results.cta_step")}</div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold">{t("results.cta_h")}</h2>
              <p className="mt-3 text-slate-300 max-w-2xl">
                {t("results.cta_sub")}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                {calendarUrl && (
                  <a href={calendarUrl} target="_blank" rel="noreferrer" className="btn-primary" data-testid="cta-expert-calendar">
                    <Calendar className="h-4 w-4" /> {t("results.cta_book")}
                  </a>
                )}
                <a href={`mailto:${config?.contact_email || "matthieu.filizzola@ariovis.fr"}`} className="btn-ghost border-slate-700 text-slate-100 hover:text-brand-fuchsia">
                  <Mail className="h-4 w-4" /> {t("results.cta_email")}
                </a>
              </div>
            </div>
            <div className="text-sm text-slate-300">
              <div className="label-caps text-slate-500">{t("results.cta_contact")}</div>
              <div className="mt-2 font-bold text-white">Matthieu Filizzola</div>
              <div>Ariovis · Netwrix</div>
              <div className="mt-3 flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {config?.contact_email || "matthieu.filizzola@ariovis.fr"}</div>
              <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {config?.contact_phone || "07 69 50 94 18"}</div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
